<template>
  <div class="money-input-container">
    <label v-if="label" class="money-input-label">{{ label }}</label>
    <div class="money-input" :class="{ 'money-input--focused': isFocused, 'money-input--disabled': disabled }">
      <span class="money-prefix">$</span>
      <input
        class="money-input-field"
        type="text"
        inputmode="decimal"
        :placeholder="placeholder"
        :value="modelValue"
        :disabled="disabled"
        autocomplete="nope"
        data-lpignore="true"
        data-form-type="other"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @keydown="handleKeydown"
        ref="input"
      />
      <span v-if="suffix" class="money-suffix">{{ suffix }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MoneyInput',
  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '0' },
    suffix: { type: String, default: '/mo' },
    disabled: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  data() {
    return { isFocused: false }
  },
  methods: {
    handleKeydown(event) {
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End']
      const isDigit = /^\d$/.test(event.key)
      const isDot = event.key === '.'
      const hasModifier = event.ctrlKey || event.metaKey || event.altKey
      const alreadyHasDot = (this.modelValue || '').includes('.')

      if (!isDigit && !(isDot && !alreadyHasDot) && !allowedKeys.includes(event.key) && !hasModifier) {
        event.preventDefault()
      }
    },
    handleInput(event) {
      let value = event.target.value
      // Strip anything that isn't a digit or decimal point
      value = value.replace(/[^\d.]/g, '')
      // Only allow one decimal point
      const parts = value.split('.')
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('')
      }
      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].slice(0, 2)
      }
      this.$emit('update:modelValue', value)
    }
  }
}
</script>

<style scoped>
.money-input-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.money-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.money-input {
  display: flex;
  align-items: center;
  min-height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  transition: all 0.15s ease;
  overflow: hidden;
}

.money-input:hover:not(.money-input--disabled) {
  border-color: #9ca3af;
}

.money-input--focused {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.money-input--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.money-prefix {
  padding-left: 12px;
  font-size: 1rem;
  font-weight: 500;
  color: #6b7280;
  flex-shrink: 0;
  user-select: none;
}

.money-suffix {
  padding-right: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #9ca3af;
  flex-shrink: 0;
  user-select: none;
}

.money-input-field {
  width: 100%;
  height: 100%;
  padding: 0 6px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  box-sizing: border-box;
}

.money-input-field::placeholder {
  color: #d1d5db;
}

.money-input-field:disabled {
  cursor: not-allowed;
}
</style>
